import { WallsGame, BlockPath } from './WallsGame.js';
import { Grid } from '../../core/Grid.js';
import {
    Direction, Edge, Position, positionKey, edgeAdjacentCells, isInside
} from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, staggerDelay, turnDelta } from '../../rendering/juice.js';

export interface WallsRenderConfig extends Partial<RenderConfig> {
    wallColor?: string;
    invalidWallColor?: string;
    unhitWallColor?: string;
    wallWidth?: number;
    pathWidth?: number;
    arrowSize?: number;
}

/**
 * Radius of the start ring, as a fraction of the cell size. Shared with WallsInput so
 * the clickable area for turning the start matches the ring the player actually sees.
 */
export const START_RING_RADIUS = 0.32;

const WALLS_DEFAULTS = {
    wallColor: COLORS.teal,
    invalidWallColor: COLORS.red,
    unhitWallColor: COLORS.amber,
    wallWidth: 4,
    pathWidth: 2,
    arrowSize: 8
};

/** Unordered key for the pair of cells a path step joins. */
function pairKey(a: Position, b: Position): string {
    const keyA = positionKey(a);
    const keyB = positionKey(b);
    return keyA < keyB ? `${keyA}|${keyB}` : `${keyB}|${keyA}`;
}

interface PathAnalysis {
    lastCorrectIdx: number;           // position index of last sequential digit (-1 if none)
    firstViolationIdx: number;        // position index of first wrong/double-hit digit (-1 if none)
    digitColors: Map<number, string>; // per-digit color: teal for correct, red for violation
}

// ── Wall animation (docs/juice.md) ────────────────────────────────────
//
// A wall is a signpost with one scalar: how much of it exists, measured from the
// edge centre outward, *signed* by which way it points. The two directions an
// edge can hold are always opposite, so all three transitions are that one
// number moving — 0 → 1 to place, +1 → -1 to flip end-for-end (collapsing to
// nothing and re-emerging the other way), 1 → 0 to remove.

const OPPOSITE_DIR: Record<Direction, Direction> = { N: 'S', S: 'N', E: 'W', W: 'E' };

interface WallTween {
    kind: 'enter' | 'change' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    /** Where it points; for a flip, the direction it is turning *away* from. */
    dir: Direction;
    /** Exit only: the colour it had on the last frame it existed. */
    color: string;
}

export class WallsRenderer extends GridRenderer {
    private wallsConfig: typeof WALLS_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private wallTweens = new Map<string, WallTween>();
    private wallState = new Map<string, Direction>();   // what the last frame drew
    private wallColors = new Map<string, string>();     // last colour, for removals
    private wallsSynced: boolean = false;               // first frame seeds, never animates
    /** The cells whose block crossing is replaying — the two either side of the
     *  wall just changed — and when it started (docs/juice.md). */
    private blockPlay: { cells: Position[]; start: number } | null = null;
    private startAngle: number | null = null;           // running, unwrapped arrow angle
    private startAnim: { from: number; to: number; start: number } | null = null;
    private startCellKey: string | null = null;
    private startDir: Direction | null = null;
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: WallsGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: WallsRenderConfig = {}) {
        super(canvas, grid, config);
        this.wallsConfig = { ...WALLS_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every wall and the start marker at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.wallTweens.clear();
        this.startAnim = null;
        this.blockPlay = null;
    }

    render(game: WallsGame): void {
        const grid = game.getGrid();
        const path = game.getPath();
        const isWin = game.checkWin();
        const analysis = path ? this.analyzePath(game, path) : null;

        this.lastGame = game;
        this.animating = false;
        this.syncStartTween(game, this.tweens.now());
        // Before the path: the diff is what starts the block replay, and the path
        // is the first thing drawn.
        this.syncWallTweens(game.getWalls());

        this.clear();
        this.drawGrid();

        // The two cells either side of the wall just changed replay the block's way
        // through them. The path is drawn as long runs between cell centres, so
        // those cells are clipped out of it and their own crossings drawn
        // separately (docs/juice.md).
        const replay = this.blockReplay();
        if (replay) {
            this.ctx.save();
            this.clipOutCells(replay.cells);
        }
        this.drawPath(game, path, analysis, isWin);
        if (replay) {
            this.ctx.restore();
            for (const cell of replay.cells) {
                this.drawBlockCrossing(path, analysis, isWin, cell, replay.p);
            }
        }
        this.drawOverlapMarkers(path, analysis, isWin);
        this.drawStuckWarning(path, isWin);
        // Red duplicate-warning borders go under the start marker and digit arrows:
        // both must stay legible on a cell flagged as crossed twice.
        this.drawDuplicateWarnings(game, path);
        this.drawStartMarker(game);
        this.drawDigitIndicators(game, path, analysis, isWin);
        this.drawWrapIndicators(path, analysis, isWin);
        this.drawWalls(game);
        if (isVortexGame(game)) { this.drawActiveVortexGates(game); }
        this.drawDigits(grid);

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Path Analysis ─────────────────────────────────────────────────

    private analyzePath(game: WallsGame, path: BlockPath): PathAnalysis {
        const grid = game.getGrid();
        const digitColors = new Map<number, string>();
        let lastCorrectIdx = -1;
        let firstViolationIdx = -1;

        const digitsHit = path.digitsHit;
        if (digitsHit.length === 0) {
            return { lastCorrectIdx, firstViolationIdx, digitColors };
        }

        // The correct chain, straight from the game so the teal digits here are
        // the same ones the status bar counts.
        const { start: bestRunStart, length: bestRunLength } = game.getBestDigitRun(digitsHit);

        const correctDigits = new Set<number>();
        for (let i = bestRunStart; i < bestRunStart + bestRunLength; i++) {
            correctDigits.add(digitsHit[i]);
        }

        // Walk positions to find position indices for color transitions
        const seenInPath = new Set<number>();

        for (let i = 0; i < path.positions.length; i++) {
            const digit = grid.getDigitAt(path.positions[i]);
            if (digit === null) continue;

            if (seenInPath.has(digit)) {
                // Second crossing — always a violation
                if (firstViolationIdx === -1) firstViolationIdx = i;
            } else {
                seenInPath.add(digit);
                if (correctDigits.has(digit)) {
                    digitColors.set(digit, COLORS.teal);
                    lastCorrectIdx = i;
                } else {
                    digitColors.set(digit, COLORS.red);
                    if (firstViolationIdx === -1) firstViolationIdx = i;
                }
            }
        }

        return { lastCorrectIdx, firstViolationIdx, digitColors };
    }

    // ── Cell replay (docs/juice.md) ───────────────────────────────────

    /** The cells replaying the block's crossing this frame, and how far in they are. */
    private blockReplay(): { cells: Position[]; p: number } | null {
        if (!this.blockPlay) return null;
        const { value, done } = this.tweens.tween(this.blockPlay.start, MOTION.replay);
        if (done) {
            this.blockPlay = null;
            return null;
        }
        this.animating = true;
        return { cells: this.blockPlay.cells, p: value };
    }

    /**
     * The block's crossing of one cell, `p` of the way through: in from the edge it
     * enters to the centre, then out by the edge it leaves. A cell the block visits
     * twice plays both visits.
     */
    private drawBlockCrossing(
        path: BlockPath | null, analysis: PathAnalysis | null, isWin: boolean,
        cell: Position, p: number
    ): void {
        if (!path || path.positions.length < 2) return;
        const n = path.positions.length;
        const teleports = path.teleportIndices ? new Set(path.teleportIndices) : null;

        this.ctx.lineWidth = this.wallsConfig.pathWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.setLineDash([4, 4]);

        const stroke = (pieces: Array<[{ x: number; y: number }, { x: number; y: number }]>) => {
            for (const [a, b] of pieces) {
                this.ctx.beginPath();
                this.ctx.moveTo(a.x, a.y);
                this.ctx.lineTo(b.x, b.y);
                this.ctx.stroke();
            }
        };

        for (let i = 0; i < n; i++) {
            const pos = path.positions[i];
            if (pos.row !== cell.row || pos.col !== cell.col) continue;

            // The loop wraps round at its ends when it's closed; an open path just
            // has nothing on the far side.
            const prevIdx = i > 0 ? i - 1 : (path.loopClosed ? n - 1 : -1);
            const nextIdx = i < n - 1 ? i + 1 : (path.loopClosed ? 0 : -1);
            // A teleport arrival/departure is a purple jump, not a lane, so it has
            // no half to draw here.
            const hasIn = prevIdx >= 0 && !teleports?.has(i);
            const hasOut = nextIdx >= 0 && !(nextIdx > 0 && teleports?.has(nextIdx));
            if (!hasIn && !hasOut) continue;

            const inDir = hasIn ? this.stepDir(path.positions[prevIdx], pos) : null;
            const outDir = hasOut ? this.stepDir(pos, path.positions[nextIdx]) : null;

            // Each half takes the colour of the run it belongs to: the segment
            // arriving at `i`, and the one arriving at the cell after it (the
            // closing segment being indexed one past the end, as drawPath has it).
            if (inDir) {
                this.ctx.strokeStyle = this.getSegmentColor(i, analysis, isWin);
                stroke(this.crossingHalves(cell, inDir, outDir ?? inDir, p).slice(0, 1));
            }
            if (outDir) {
                this.ctx.strokeStyle = this.getSegmentColor(i === n - 1 ? n : i + 1, analysis, isWin);
                stroke(this.crossingHalves(cell, inDir ?? outDir, outDir, p).slice(1));
            }
        }

        this.ctx.setLineDash([]);
    }

    /** Heading from one path cell to the next, reading a jump of more than one cell
     *  as the toroidal wrap it is. */
    private stepDir(a: Position, b: Position): Direction {
        let dRow = b.row - a.row;
        let dCol = b.col - a.col;
        if (dRow > 1) dRow = -1; else if (dRow < -1) dRow = 1;
        if (dCol > 1) dCol = -1; else if (dCol < -1) dCol = 1;
        return dRow < 0 ? 'N' : dRow > 0 ? 'S' : dCol < 0 ? 'W' : 'E';
    }

    private getSegmentColor(
        segIdx: number,
        analysis: PathAnalysis | null,
        isWin: boolean
    ): string {
        if (isWin || !analysis) return COLORS.teal;
        if (analysis.firstViolationIdx >= 0 && segIdx > analysis.firstViolationIdx) return COLORS.red;
        if (analysis.lastCorrectIdx >= 0 && segIdx > analysis.lastCorrectIdx) return COLORS.amber;
        return COLORS.teal;
    }

    // ── Direction Inference ───────────────────────────────────────────

    private inferDirection(prev: Position, curr: Position): Direction {
        const rowDiff = curr.row - prev.row;
        const colDiff = curr.col - prev.col;

        // Handle wrapping (large jumps)
        if (Math.abs(colDiff) > 1) return colDiff < 0 ? 'E' : 'W';
        if (Math.abs(rowDiff) > 1) return rowDiff < 0 ? 'S' : 'N';

        // Normal movement
        if (colDiff === 1) return 'E';
        if (colDiff === -1) return 'W';
        if (rowDiff === 1) return 'S';
        return 'N';
    }

    // ── Edge Center ───────────────────────────────────────────────────

    private getEdgeCenter(edge: Edge): { x: number; y: number } {
        const { padding, cellSize } = this.config;
        const midRow = (edge.pa.row + edge.pb.row) / 2;
        const midCol = (edge.pa.col + edge.pb.col) / 2;
        return {
            x: padding + midCol * cellSize,
            y: padding + midRow * cellSize
        };
    }

    private static readonly DIR_ANGLE: Record<Direction, number> = {
        'E': 0,
        'S': Math.PI / 2,
        'W': Math.PI,
        'N': -Math.PI / 2
    };

    // ── Wall Drawing ──────────────────────────────────────────────────

    // Draw a single wall with background mask, bar and arrow tip.
    // Everything is drawn pointing right then rotated into direction.
    // `extent` (0..1) is how much of the wall exists, growing from the edge centre
    // outward — 1 at rest, part-way through a place, flip or remove (docs/juice.md).
    private drawWallElement(
        center: { x: number; y: number },
        direction: Direction,
        halfLenAtRest: number,
        color: string,
        extent: number = 1
    ): void {
        const { wallWidth } = this.wallsConfig;
        const halfLen = halfLenAtRest * extent;
        const arrowSize = this.wallsConfig.arrowSize * extent;

        this.ctx.save();
        this.ctx.translate(center.x, center.y);
        this.ctx.rotate(WallsRenderer.DIR_ANGLE[direction]);
        this.ctx.lineCap = 'round';

        // Background mask (bar + arrow tip)
        this.ctx.strokeStyle = COLORS.background;
        this.ctx.fillStyle = COLORS.background;
        this.ctx.lineWidth = wallWidth + 4;
        this.ctx.beginPath();
        this.ctx.moveTo(-halfLen, 0);
        this.ctx.lineTo(halfLen, 0);
        this.ctx.stroke();
        const bgSize = arrowSize + 2;
        this.ctx.beginPath();
        this.ctx.moveTo(halfLen + bgSize, 0);
        this.ctx.lineTo(halfLen - bgSize * 0.3, -bgSize * 1.2);
        this.ctx.lineTo(halfLen - bgSize * 0.3, bgSize * 1.2);
        this.ctx.closePath();
        this.ctx.fill();

        // Colored bar + arrow tip
        this.ctx.strokeStyle = color;
        this.ctx.fillStyle = color;
        this.ctx.lineWidth = wallWidth;
        this.ctx.beginPath();
        this.ctx.moveTo(-halfLen, 0);
        this.ctx.lineTo(halfLen, 0);
        this.ctx.stroke();
        this.ctx.beginPath();
        this.ctx.moveTo(halfLen + arrowSize, 0);
        this.ctx.lineTo(halfLen - arrowSize * 0.3, -arrowSize * 0.6);
        this.ctx.lineTo(halfLen - arrowSize * 0.3, arrowSize * 0.6);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.restore();
    }

    private drawWalls(game: WallsGame, colorOverride?: string): void {
        const walls = game.getWalls();
        const invalidWalls = game.getInvalidWalls();
        // Walls the block never redirects off are stray pieces: amber, like Mirrors'
        // unhit mirrors, and they keep the puzzle from being solved.
        const wallsHit = game.getPath()?.wallsHit ?? new Set<string>();
        const halfLen = this.config.cellSize * 0.3;

        // The scoring overlay redraws the walls in a flat colour on a frozen
        // board: it reads state, it doesn't advance it.
        const live = colorOverride === undefined;
        if (live) this.syncWallTweens(walls);
        const now = this.tweens.now();

        for (const [key, direction] of walls) {
            const edge = this.grid.deserializeEdge(key);
            if (!edge) continue;

            const isInvalid = invalidWalls.has(key);
            const isUnhit = !wallsHit.has(key);
            const color = colorOverride ?? (
                isInvalid ? this.wallsConfig.invalidWallColor
                : isUnhit ? this.wallsConfig.unhitWallColor
                : this.wallsConfig.wallColor
            );
            if (live) this.wallColors.set(key, color);

            const tween = live ? this.wallTweens.get(key) : undefined;
            const shape = tween ? this.wallShape(tween, now) : { dir: direction, extent: 1, done: true };
            if (tween && shape.done) this.wallTweens.delete(key);
            else if (tween) this.animating = true;

            this.drawWallElement(this.getEdgeCenter(edge), shape.dir, halfLen, color, shape.extent);
        }

        // Walls being removed are already gone from game state — they only exist
        // here, retracting in the colour they wore when they went.
        for (const [key, tween] of live ? this.wallTweens : []) {
            if (tween.kind !== 'exit' || walls.has(key)) continue;
            const edge = this.grid.deserializeEdge(key);
            const shape = this.wallShape(tween, now);
            if (shape.done) {
                this.wallTweens.delete(key);
                this.wallColors.delete(key);
                continue;
            }
            this.animating = true;
            if (edge) {
                this.drawWallElement(this.getEdgeCenter(edge), shape.dir, halfLen, tween.color, shape.extent);
            }
        }
    }

    /**
     * Diff the board against the last frame and start a tween per changed edge:
     * placed walls grow out of the edge centre, a cycled one collapses and
     * re-emerges pointing the other way, a removed one retracts. A bulk change
     * (Reset, Restore best) fans out in reading order rather than popping at once.
     */
    private syncWallTweens(walls: Map<string, Direction>): void {
        if (!this.tweens.isEnabled()) {
            this.wallTweens.clear();
            this.wallState = new Map(walls);
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a
        // saved solution would replay the whole thing.
        if (!this.wallsSynced) {
            this.wallState = new Map(walls);
            this.wallsSynced = true;
            return;
        }

        const changed: Array<{ key: string; prev?: Direction; next?: Direction }> = [];
        for (const [key, next] of walls) {
            const prev = this.wallState.get(key);
            if (prev !== next) changed.push({ key, prev, next });
        }
        for (const [key, prev] of this.wallState) {
            if (!walls.has(key)) changed.push({ key, prev });
        }
        if (changed.length === 0) return;

        // Reading order: a wall loop has no natural origin to fan out from, so
        // top-left to bottom-right is the honest choice. Edge keys are serialized
        // points, so rank off the geometry rather than the string.
        const rank = (key: string) => {
            const edge = this.grid.deserializeEdge(key);
            return edge ? edge.pa.row * this.grid.size + edge.pa.col : 0;
        };
        changed.sort((a, b) => rank(a.key) - rank(b.key));

        const now = this.tweens.now();
        // One wall changing is a click, and the two cells it sits between replay the
        // block's way through them — that is where the redirect shows. Digit cells
        // are skipped: the path is gapped over them, so there is nothing to replay.
        // A bulk change (Reset, Restore best) isn't a question about any one wall.
        this.blockPlay = null;
        if (changed.length === 1) {
            const edge = this.grid.deserializeEdge(changed[0].key);
            const cells = edge
                ? edgeAdjacentCells(edge).filter(c => isInside(c) && !this.grid.hasDigitAt(c))
                : [];
            if (cells.length > 0) this.blockPlay = { cells, start: now };
        }

        changed.forEach(({ key, prev, next }, i) => {
            const start = now + staggerDelay(i, changed.length);
            if (next === undefined) {
                this.wallTweens.set(key, {
                    kind: 'exit', start, dir: prev!,
                    color: this.wallColors.get(key) ?? this.wallsConfig.unhitWallColor
                });
            } else if (prev === undefined) {
                this.wallTweens.set(key, { kind: 'enter', start, dir: next, color: '' });
            } else {
                // The two directions an edge can hold are opposite, so a cycle is
                // always an end-for-end flip: it turns away from `prev`.
                this.wallTweens.set(key, { kind: 'change', start, dir: prev, color: '' });
            }
        });

        this.wallState = new Map(walls);
    }

    /** Which way the wall points this frame and how much of it is on the board. */
    private wallShape(tween: WallTween, now: number): { dir: Direction; extent: number; done: boolean } {
        const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
        switch (tween.kind) {
            case 'enter':
                return { dir: tween.dir, extent: value, done };
            case 'exit':
                // The role's curve runs the *disappearance*, so invert it to get
                // what's left on the board.
                return { dir: tween.dir, extent: 1 - value, done };
            case 'change': {
                // +1 → -1: it shrinks to nothing and grows back the other way.
                const signed = 1 - 2 * value;
                return signed >= 0
                    ? { dir: tween.dir, extent: signed, done }
                    : { dir: OPPOSITE_DIR[tween.dir], extent: -signed, done };
            }
        }
    }

    /** Scoring overlay: redraw the walls (primary) in amber. */
    protected drawScoringPrimary(game: WallsGame): void {
        this.drawWalls(game, COLORS.amber);
    }

    // ── Path Drawing ──────────────────────────────────────────────────

    /**
     * Start a tween when the ring is turned. Only when the start *cell* stays
     * put: the cell is auto-selected, so it moving is a consequence of the board
     * changing, and sliding the marker across the grid would animate a deduction
     * rather than an action. Turning it in place is the one thing the player does
     * to it directly.
     */
    private syncStartTween(game: WallsGame, now: number): void {
        const pos = game.getStartPosition();
        const dir = game.getStartDirection();
        if (!pos || !dir) {
            this.startCellKey = null;
            this.startDir = null;
            this.startAnim = null;
            return;
        }

        const cellKey = positionKey(pos);
        const target = WallsRenderer.DIR_ANGLE[dir];
        const sameCell = cellKey === this.startCellKey;

        if (!sameCell || this.startDir === null || !this.tweens.isEnabled()) {
            this.startAngle = target;
            this.startAnim = null;
        } else if (dir !== this.startDir) {
            // The ring's own cycle is N → E → S → W, so a half-turn goes clockwise.
            const from = this.startAngleAt(now);
            this.startAngle = from + turnDelta(from, target, 1);
            this.startAnim = { from, to: this.startAngle, start: now };
        }

        this.startCellKey = cellKey;
        this.startDir = dir;
    }

    /** The start arrow's angle this frame, mid-turn or at rest. */
    private startAngleAt(now: number): number {
        const rest = this.startAngle ?? 0;
        if (!this.startAnim) return rest;

        // `change`: the marker is already on the board and turns in place.
        const { value, done } = this.tweens.tween(this.startAnim.start, MOTION.change, now);
        if (done) {
            this.startAnim = null;
            return rest;
        }
        this.animating = true;
        const { from, to } = this.startAnim;
        return from + (to - from) * value;
    }

    private drawStartMarker(game: WallsGame): void {
        const startPos = game.getStartPosition();
        const startDir = game.getStartDirection();
        if (!startPos || !startDir) return;

        const start = this.getCellCenter(startPos);

        // Start ring (background fill + teal stroke), sized like Knight's digit
        // circles so a digit under the start stays legible inside the ring.
        const ringR = this.config.cellSize * START_RING_RADIUS;
        this.ctx.beginPath();
        this.ctx.arc(start.x, start.y, ringR, 0, Math.PI * 2);
        this.ctx.fillStyle = COLORS.background;
        this.ctx.fill();
        this.ctx.lineWidth = 2.5;
        this.ctx.strokeStyle = COLORS.teal;
        this.ctx.stroke();

        // Direction arrow, sitting just outside the ring — it swings round the
        // ring when the player turns the start.
        const angle = this.startAngleAt(this.tweens.now());
        const dx = Math.cos(angle), dy = Math.sin(angle);

        const base = ringR;       // triangle base on the ring edge
        const tip = ringR + 8;    // tip pointing outward
        const hw = 5;             // base half-width
        this.ctx.fillStyle = COLORS.teal;
        this.ctx.beginPath();
        this.ctx.moveTo(start.x + dx * tip, start.y + dy * tip);
        this.ctx.lineTo(start.x + dx * base - dy * hw, start.y + dy * base + dx * hw);
        this.ctx.lineTo(start.x + dx * base + dy * hw, start.y + dy * base - dx * hw);
        this.ctx.closePath();
        this.ctx.fill();
    }

    private drawPath(
        _game: WallsGame,
        path: BlockPath | null,
        analysis: PathAnalysis | null,
        isWin: boolean
    ): void {
        if (!path || path.positions.length < 2) return;

        const { pathWidth } = this.wallsConfig;

        const teleports = path.teleportIndices ? new Set(path.teleportIndices) : null;
        const teleportSegments: Array<{ from: Position; to: Position }> = [];

        this.ctx.lineWidth = pathWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.setLineDash([4, 4]);

        // Draw path segments with per-segment coloring, gapped over digit cells
        // (their entry/exit triangles mark the block's passage instead).
        for (let i = 1; i < path.positions.length; i++) {
            const pos = path.positions[i];
            const prev = path.positions[i - 1];

            // Check if this is a teleport jump
            if (teleports?.has(i)) {
                teleportSegments.push({ from: prev, to: pos });
                continue;
            }

            this.ctx.strokeStyle = this.getSegmentColor(i, analysis, isWin);
            this.drawBlockSegment(this.getCellCenter(prev), this.getCellCenter(pos), pos.row - prev.row, pos.col - prev.col);
        }

        // Draw closing segment if loop is closed
        if (path.loopClosed && path.positions.length > 0) {
            const lastPos = path.positions[path.positions.length - 1];
            const firstPos = path.positions[0];

            // Closing segment color: same as last segment
            this.ctx.strokeStyle = this.getSegmentColor(path.positions.length, analysis, isWin);
            this.drawBlockSegment(
                this.getCellCenter(lastPos), this.getCellCenter(firstPos),
                firstPos.row - lastPos.row, firstPos.col - lastPos.col
            );
        }

        this.ctx.setLineDash([]);

        // Draw dashed purple teleport lines
        if (teleportSegments.length > 0) {
            this.ctx.strokeStyle = COLORS.purple;
            this.ctx.lineWidth = 3;
            this.ctx.setLineDash([6, 4]);
            this.ctx.lineCap = 'round';

            for (const seg of teleportSegments) {
                const from = this.getCellCenter(seg.from);
                const to = this.getCellCenter(seg.to);
                this.ctx.beginPath();
                this.ctx.moveTo(from.x, from.y);
                this.ctx.lineTo(to.x, to.y);
                this.ctx.stroke();
            }

            this.ctx.setLineDash([]);
        }
    }

    // ── Overlap Markers ───────────────────────────────────────────────

    /**
     * Walk every step of the path (including the closing one on a shut loop),
     * skipping teleport jumps. `visit` gets the pair of cells the step joins.
     */
    private forEachStep(
        path: BlockPath,
        visit: (from: Position, to: Position, segIdx: number) => void
    ): void {
        const teleports = path.teleportIndices ? new Set(path.teleportIndices) : null;

        for (let i = 1; i < path.positions.length; i++) {
            if (teleports?.has(i)) continue;
            visit(path.positions[i - 1], path.positions[i], i);
        }

        if (path.loopClosed && path.positions.length > 1) {
            const last = path.positions[path.positions.length - 1];
            visit(last, path.positions[0], path.positions.length);
        }
    }

    /**
     * Cell pairs the block runs along in both directions, so their two path
     * segments are drawn on top of each other. Travelling the same pair twice the
     * same way just retraces one line and needs no marker.
     */
    private getDoubledPairs(path: BlockPath): Set<string> {
        const directions = new Map<string, Set<Direction>>();
        const doubled = new Set<string>();

        this.forEachStep(path, (from, to) => {
            const key = pairKey(from, to);
            let dirs = directions.get(key);
            if (!dirs) {
                dirs = new Set();
                directions.set(key, dirs);
            }
            dirs.add(this.inferDirection(from, to));
            if (dirs.size > 1) doubled.add(key);
        });

        return doubled;
    }

    /**
     * Mark each segment of a doubled pair with a half-triangle pointing the way
     * the block travels, offset to that direction's right so the two runs read
     * apart: eastward below the line, westward above it.
     */
    private drawOverlapMarkers(
        path: BlockPath | null,
        analysis: PathAnalysis | null,
        isWin: boolean
    ): void {
        if (!path || path.positions.length < 2) return;

        const doubled = this.getDoubledPairs(path);
        if (doubled.size === 0) return;

        this.forEachStep(path, (from, to, segIdx) => {
            if (!doubled.has(pairKey(from, to))) return;
            this.drawOverlapMarker(from, to, this.getSegmentColor(segIdx, analysis, isWin));
        });
    }

    /** One direction marker for the step from `from` to `to`. */
    private drawOverlapMarker(from: Position, to: Position, color: string): void {
        const { padding, cellSize } = this.config;
        const gridSize = cellSize * this.grid.size;
        const rowDiff = to.row - from.row;
        const colDiff = to.col - from.col;
        const fromCenter = this.getCellCenter(from);
        const toCenter = this.getCellCenter(to);

        if (Math.abs(rowDiff) > 1 || Math.abs(colDiff) > 1) {
            // Wrapped move: mark the piece running off the rim, which carries the
            // same direction as the move itself.
            const exit = Math.abs(rowDiff) > 1
                ? { x: fromCenter.x, y: rowDiff > 0 ? padding : padding + gridSize }
                : { x: colDiff > 0 ? padding : padding + gridSize, y: fromCenter.y };
            this.fillDirectionMarker(fromCenter, exit, color);
            return;
        }

        this.fillDirectionMarker(fromCenter, toCenter, color);
    }

    /**
     * Fill a half-equilateral triangle just shy of `b`: its flat edge lies along
     * a→b and it bulges to the right of travel, apex pointing at b.
     */
    private fillDirectionMarker(
        a: { x: number; y: number }, b: { x: number; y: number }, color: string
    ): void {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const length = Math.hypot(dx, dy);
        if (length < 1) return;

        const ux = dx / length;
        const uy = dy / length;
        // Right-hand normal, with screen y growing downward: east → south, west → north.
        const nx = -uy;
        const ny = ux;

        const height = this.config.cellSize * 0.22;
        const halfBase = height / Math.sqrt(3);  // half of an equilateral triangle
        const tipGap = this.config.cellSize * 0.1;

        const apex = { x: b.x - ux * tipGap, y: b.y - uy * tipGap };
        const base = { x: apex.x - ux * height, y: apex.y - uy * height };
        const corner = { x: base.x + nx * halfBase, y: base.y + ny * halfBase };

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.moveTo(apex.x, apex.y);
        this.ctx.lineTo(base.x, base.y);
        this.ctx.lineTo(corner.x, corner.y);
        this.ctx.closePath();
        this.ctx.fill();
    }

    /**
     * Stroke one block-path segment (between two cell centres) in the current
     * style, splitting a wrapped move into two edge-bound pieces and gapping every
     * piece where it passes over a digit cell.
     */
    private drawBlockSegment(
        prevCenter: { x: number; y: number }, posCenter: { x: number; y: number },
        rowDiff: number, colDiff: number
    ): void {
        const { padding, cellSize } = this.config;
        const gridSize = cellSize * this.grid.size;

        if (Math.abs(rowDiff) > 1 || Math.abs(colDiff) > 1) {
            // Wrapped move: prev → its edge, then the opposite edge → pos.
            let exit: { x: number; y: number }, entry: { x: number; y: number };
            if (Math.abs(rowDiff) > 1) {
                const prevY = rowDiff > 0 ? padding : padding + gridSize;
                const posY = rowDiff > 0 ? padding + gridSize : padding;
                exit = { x: prevCenter.x, y: prevY };
                entry = { x: posCenter.x, y: posY };
            } else {
                const prevX = colDiff > 0 ? padding : padding + gridSize;
                const posX = colDiff > 0 ? padding + gridSize : padding;
                exit = { x: prevX, y: prevCenter.y };
                entry = { x: posX, y: posCenter.y };
            }
            this.strokeLineClippedAtDigits(prevCenter, exit);
            this.strokeLineClippedAtDigits(entry, posCenter);
        } else {
            this.strokeLineClippedAtDigits(prevCenter, posCenter);
        }
    }

    // ── Digit Indicators ──────────────────────────────────────────────

    private drawDigitIndicators(
        game: WallsGame,
        path: BlockPath | null,
        analysis: PathAnalysis | null,
        isWin: boolean
    ): void {
        if (!path || path.positions.length < 2) return;

        const grid = game.getGrid();
        const teleports = path.teleportIndices ? new Set(path.teleportIndices) : null;

        // Active vortex gate cells also get entry/exit triangles
        const activeGateKeys = new Set<string>();
        if (isVortexGame(game)) {
            for (const key of game.getActiveGatePairs().keys()) {
                activeGateKeys.add(key);
            }
        }

        for (let i = 1; i < path.positions.length; i++) {
            const pos = path.positions[i];
            const digit = grid.getDigitAt(pos);
            if (digit === null && !activeGateKeys.has(positionKey(pos))) continue;

            // Skip teleport destinations (no meaningful direction)
            if (teleports?.has(i)) continue;

            // Entry triangle — color matches arriving segment
            const entryDir = this.inferDirection(path.positions[i - 1], pos);
            const entryColor = this.getSegmentColor(i, analysis, isWin);
            this.drawEdgeTriangle(pos, entryDir, entryColor, true);

            // Exit triangle — color matches departing segment
            if (i + 1 < path.positions.length && !teleports?.has(i + 1)) {
                const exitDir = this.inferDirection(pos, path.positions[i + 1]);
                const exitColor = this.getSegmentColor(i + 1, analysis, isWin);
                this.drawEdgeTriangle(pos, exitDir, exitColor, false);
            }
        }
    }

    // ── Wrap Indicators ────────────────────────────────────────────────

    private drawWrapIndicators(
        path: BlockPath | null,
        analysis: PathAnalysis | null,
        isWin: boolean
    ): void {
        if (!path || path.positions.length < 2) return;

        const teleports = path.teleportIndices ? new Set(path.teleportIndices) : null;

        const drawWrapForSegment = (prev: Position, curr: Position, segIdx: number) => {
            const rowDiff = curr.row - prev.row;
            const colDiff = curr.col - prev.col;
            if (Math.abs(rowDiff) <= 1 && Math.abs(colDiff) <= 1) return;

            const color = this.getSegmentColor(segIdx, analysis, isWin);
            const dir = this.inferDirection(prev, curr);

            // Exit arrow at exit edge (from prev cell)
            this.drawGridEdgeTriangle(prev, dir, color, false);
            // Entry arrow at entry edge (into curr cell)
            this.drawGridEdgeTriangle(curr, dir, color, true);
        };

        for (let i = 1; i < path.positions.length; i++) {
            if (teleports?.has(i)) continue;
            drawWrapForSegment(path.positions[i - 1], path.positions[i], i);
        }

        // Closing loop segment
        if (path.loopClosed && path.positions.length > 0) {
            const last = path.positions[path.positions.length - 1];
            const first = path.positions[0];
            drawWrapForSegment(last, first, path.positions.length);
        }
    }

    // Draw a small triangle at the grid boundary for wrap entry/exit.
    // Same style as drawEdgeTriangle but positioned at the grid rim.
    private drawGridEdgeTriangle(
        cellPos: Position, travelDir: Direction, color: string, entry: boolean
    ): void {
        const { padding, cellSize } = this.config;
        const gridPixels = cellSize * this.grid.size;
        const center = this.getCellCenter(cellPos);

        let x: number, y: number;
        let angle: number;

        if (entry) {
            // Entry: triangle at the edge the block enters from (opposite of travel dir)
            switch (travelDir) {
                case 'E': x = padding; y = center.y; angle = -Math.PI / 2; break;
                case 'W': x = padding + gridPixels; y = center.y; angle = Math.PI / 2; break;
                case 'S': x = center.x; y = padding; angle = 0; break;
                case 'N': x = center.x; y = padding + gridPixels; angle = Math.PI; break;
            }
        } else {
            // Exit: triangle at the edge the block leaves from (same as travel dir)
            switch (travelDir) {
                case 'E': x = padding + gridPixels; y = center.y; angle = -Math.PI / 2; break;
                case 'W': x = padding; y = center.y; angle = Math.PI / 2; break;
                case 'S': x = center.x; y = padding + gridPixels; angle = 0; break;
                case 'N': x = center.x; y = padding; angle = Math.PI; break;
            }
        }

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        if (entry) {
            this.ctx.moveTo(-5, 1);
            this.ctx.lineTo(5, 1);
            this.ctx.lineTo(0, 9);
        } else {
            this.ctx.moveTo(-5, -9);
            this.ctx.lineTo(5, -9);
            this.ctx.lineTo(0, -1);
        }
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.restore();
    }

    // ── Warnings ──────────────────────────────────────────────────────

    // Amber dot at stuck position (drawn before path so it appears below)
    private drawStuckWarning(path: BlockPath | null, isWin: boolean): void {
        if (!path || !path.stuck || !path.stuckPosition || isWin) return;

        const center = this.getCellCenter(path.stuckPosition);
        this.ctx.fillStyle = COLORS.amber;
        this.ctx.beginPath();
        this.ctx.arc(center.x, center.y, 6, 0, Math.PI * 2);
        this.ctx.fill();
    }

    // Red cell borders for digits crossed more than once
    private drawDuplicateWarnings(game: WallsGame, path: BlockPath | null): void {
        if (!path) return;

        const grid = game.getGrid();

        for (const digit of path.digitsCrossedTwice) {
            const pos = grid.getDigitPosition(digit);
            if (pos) {
                this.drawCellBorder(pos, COLORS.red);
            }
        }
    }

    // ── Edge Highlight ────────────────────────────────────────────────

    highlightEdge(edge: Edge, color: string = colorWithAlpha('white', 0.3)): void {
        const center = this.getEdgeCenter(edge);
        const { cellSize } = this.config;
        const wallLength = cellSize * 0.6;

        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.wallsConfig.wallWidth + 2;
        this.ctx.lineCap = 'round';

        this.ctx.beginPath();
        if (edge.orient === 'h') {
            this.ctx.moveTo(center.x - wallLength / 2, center.y);
            this.ctx.lineTo(center.x + wallLength / 2, center.y);
        } else {
            this.ctx.moveTo(center.x, center.y - wallLength / 2);
            this.ctx.lineTo(center.x, center.y + wallLength / 2);
        }
        this.ctx.stroke();
    }
}
